import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import type { GridAggregationModel, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { createElement } from "react";
import { BiAggregation, CalculationType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  DrillDownRow,
  FailedMeasure,
  ListDataCell,
  ReportField,
} from "../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import { isCustomColumn } from "../../../../utilities/fieldsHelper.ts";
import GrandTotalCell from "../../../viewer/common/GrandTotalCell.tsx";
import { renderColumnHeader } from "../../../viewer/tabular/utils/tabularGridDataProvider.tsx";

export const LIST_PAGE_SIZE = 500;
const emptyAggregationModel: GridAggregationModel = {};

export const toListRow = (cells: ListDataCell[]): DrillDownRow => ({
  id: generateGuid(),
  cells: Object.fromEntries(cells.map((cell) => [cell.fieldId, cell])),
});

const isNumericOutputField = (field: ReportField) =>
  (!isCustomColumn(field) || field.config.calculationType !== CalculationType.ConditionValue) &&
  field.config.aggregation !== BiAggregation.StringAgg &&
  (field.meta.type === ItemDataType.Numeric ||
    field.config.aggregation === BiAggregation.Count ||
    field.config.aggregation === BiAggregation.CountDistinct);

const showsGrandTotal = (field: ReportField) => isNumericOutputField(field) && field.config.hideAggregation !== true;

export const renderListCell = ({ aggregation, formattedValue, value, rowNode }: GridRenderCellParams<DrillDownRow>) => {
  const displayValue = String(formattedValue ?? value ?? "");
  return aggregation?.position === "footer" || rowNode.type === "pinnedRow"
    ? createElement(GrandTotalCell, { formattedValue: displayValue, disableCellClick: true })
    : displayValue;
};

export const getListColumns = (
  fields: ReportField[],
  showGrandTotal: boolean,
  failedMeasures: FailedMeasure[]
): GridColDef<DrillDownRow>[] => {
  return fields.map((field) => ({
    field: field.config.guid,
    headerName: field.config.customLabel || field.meta.caption,
    renderHeader: () =>
      renderColumnHeader(
        field.config.customLabel || field.meta.caption,
        failedMeasures.some((failure) => failure.fieldId === field.config.guid)
      ),
    width: 150,
    align: isNumericOutputField(field) ? "right" : "left",
    valueGetter: (_, row: DrillDownRow) => {
      const cell = row.cells?.[field.config.guid];
      return cell ? cell.formattedValue || cell.value : undefined;
    },
    renderCell: renderListCell,
    hideable: false,
    pinnable: false,
    groupable: false,
    filterable: false,
    aggregable: showGrandTotal && showsGrandTotal(field),
    sortable: !isCustomColumn(field),
  }));
};

export const getListAggregationModel = (fields: ReportField[], showGrandTotal: boolean): GridAggregationModel => {
  if (!showGrandTotal) {
    return emptyAggregationModel;
  }
  return Object.fromEntries(fields.filter(showsGrandTotal).map((field) => [field.config.guid, "custom"]));
};

export const getListAggregatedValue = (fields: ReportField[], row: DrillDownRow, field: string): unknown => {
  const reportField = fields.find((f) => f.config.guid === field);
  if (!reportField) {
    return undefined;
  }
  const cell = row.cells?.[reportField.config.guid];
  return cell?.formattedValue ?? cell?.value;
};
